import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'telegram-stars-subscriptions',
  title: 'Telegram Stars Subscriptions: Sell Monthly Access Without a Custom Bot',
  metaTitle: 'Telegram Stars Subscriptions: Monthly Access, No Bot',
  metaDescription:
    'How Telegram Stars subscriptions work: 30-day auto-renewal, manual periods, private channel access, the 10,000 Star cap, and setup without code.',
  keywords: [
    'telegram stars subscription',
    'telegram stars membership',
    'telegram paid subscription',
    'telegram channel subscription stars',
    'recurring telegram stars payment',
    'telegram stars monthly',
  ],
  category: 'Guides',
  datePublished: '2026-10-25',
  dateModified: '2026-10-25',
  readingTimeMin: 8,
  excerpt:
    'A Telegram Stars subscription charges a buyer on a schedule and unlocks access for that period. Telegram supports a native 30-day renewal, and you can also sell a fixed period that the buyer renews by paying again. Here is how both models work and how to launch one.',
  keyTakeaways: [
    'Automatic Stars subscriptions renew every 30 days. Telegram fixes that period; you cannot set a weekly auto-charge.',
    'Automatic subscriptions are capped at 10,000 Stars per period. A longer or custom period uses manual renewal, from 1 to 365 days.',
    'On Telegram Stars Payment Platform, a subscription invoice can deliver a file, a link, a message, or access to a private channel.',
    'The 21-day hold, the 3% platform fee and TON withdrawals apply to subscription payments the same way they apply to one-off links.',
  ],
  howTo: {
    name: 'Create a Telegram Stars subscription',
    steps: [
      { name: 'Sign in', text: 'Log in with Telegram on Telegram Stars Payment Platform.' },
      { name: 'Choose Subscription', text: 'Create an invoice and set the type to Subscription.' },
      { name: 'Pick renewal', text: 'Use automatic renewal for a 30-day charge, or manual renewal and set a duration between 1 and 365 days.' },
      { name: 'Add what access includes', text: 'Upload a file, paste an HTTPS link, write a message, or enter the private channel ID (-100…).' },
      { name: 'Share the link', text: 'Publish the payment link. Each confirmed payment grants access until the period ends.' },
    ],
  },
  body: [
    h2('What a Stars subscription actually charges'),
    p(
      'A subscription invoice is still a Stars payment. The buyer confirms a native Telegram sheet priced in Stars (currency **XTR**). The difference from a normal payment link is the period: access lasts for a set number of days, and an automatic subscription asks Telegram to charge again when that period ends.',
    ),
    p(
      'Telegram only auto-renews on a **30-day** cycle (`subscription_period` of 2,592,000 seconds). If you want a 7-day pass or a 90-day season, use a manual period. The buyer pays once, access expires, and they pay the same link again to extend it.',
    ),

    h2('Automatic vs manual renewal'),
    table(
      ['', 'Automatic', 'Manual'],
      [
        ['Period', '30 days, fixed by Telegram', '1 to 365 days, you choose'],
        ['Next charge', 'Telegram prompts renewal', 'Buyer pays the link again'],
        ['Maximum price', '10,000 Stars', 'No platform cap beyond a normal invoice'],
        ['Best for', 'Memberships, premium channels', 'Weekly passes, cohorts, seasonal access'],
      ],
    ),
    callout(
      'Price an automatic membership at or under 10,000 Stars. Above that, Telegram will not create the recurring invoice. Split a high price into a manual period, or lower the monthly amount.',
    ),

    h2('How to create one without code'),
    ol([
      '[Log in with Telegram](/login).',
      'Create an invoice and choose **Subscription**.',
      'Set **Automatic — every 30 days**, or **Manual** and enter the number of days.',
      'Add the amount in Stars, a title (32 characters or fewer) and a description that states the renewal rule in plain language.',
      'Choose what is delivered: a file, a photo, a video, an audio file, an HTTPS link, a message, or a **private channel**.',
      'Create the link and share it. A short redirect such as `telegram-stars.io/r/members` is easier to pin than the raw invoice URL.',
    ]),

    h2('Private channel access'),
    p(
      'Channel access is only available on a subscription, not on a one-off content sale. The channel must be private. You need its ID, which looks like `-100` followed by digits. Add the platform bot as an administrator with permission to invite and remove members, and keep your own Telegram account as an administrator too.',
    ),
    ul([
      'A confirmed payment marks the buyer as active until the period ends.',
      'When the period expires and they have not renewed, access is removed.',
      'Say the length and the renewal rule in the invoice description so the payment sheet matches what you promised.',
    ]),
    p('For the editorial side of a paid channel, see [how to monetize a Telegram channel with Stars](/blog/monetize-telegram-channel-with-stars).'),

    h2('What you earn, and when'),
    p(
      'Each renewal is its own Stars credit. It sits in your balance, becomes withdrawable **21 days** after that payment, and can be paid out to a TON wallet from **100 Stars**. Telegram Stars Payment Platform takes a flat **3%**, with no monthly fee. A 250 Star membership therefore nets 242.5 Stars before the Stars-to-TON rate. The rate itself is covered in [Telegram Stars fees explained](/blog/telegram-stars-fees-explained).',
    ),

    h2('What to write on the payment sheet'),
    ul([
      '**Title**: the membership name, not “invoice” or “payment”.',
      '**Description**: what they get, for how long, and whether it renews. Example: “Private channel. Renews every 30 days. Cancel any time before the next charge.”',
      '**Price**: a pack-friendly amount such as 100, 250, 500 or 1,000 Stars. Buyers often top up in those sizes.',
    ]),
    p('One-off products that should be delivered once, with no period, belong on a content sale. That flow is described in [deliver a file after a Stars payment](/blog/deliver-files-with-telegram-stars).'),
  ],
  faq: [
    {
      q: 'Can I bill every week with Telegram Stars?',
      a: 'Not automatically. Telegram auto-renews only every 30 days. For a week, create a manual subscription of 7 days and let the member pay the same link again.',
    },
    {
      q: 'What is the maximum price of an automatic Stars subscription?',
      a: '10,000 Stars per 30-day period on Telegram Stars Payment Platform, which matches Telegram’s limit for recurring Stars invoices.',
    },
    {
      q: 'Does the buyer need to start my bot first?',
      a: 'They pay inside Telegram from the link. For a private channel, the bot invites them after payment. They do not need to message you first.',
    },
    {
      q: 'Can I refund a subscription payment?',
      a: 'A Stars charge can be refunded from the seller side while Telegram still allows it. Read the hold rules in the guide to the 21-day payout hold before you promise an instant cash refund.',
    },
  ],
  relatedSlugs: [
    'monetize-telegram-channel-with-stars',
    'telegram-stars-payment-links',
    'telegram-stars-refunds-21-day-hold',
    'telegram-stars-bot-api-developer-guide',
  ],
};

export default article;
